import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, 
  LayoutDashboard, 
  Calculator, 
  CalendarCheck, 
  FileSpreadsheet, 
  HandCoins, 
  CalendarRange, 
  Landmark, 
  HelpCircle, 
  Package, 
  Users, 
  Compass, 
  ChevronRight, 
  LogOut 
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

export default function POSNavigationDrawer({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { user, userRole, logout } = useAuth();

  const getMenuItems = () => {
    if (userRole === "SALES_EXECUTIVE") {
      return [
        { label: "Dashboard", path: "/sales-executive/dashboard", icon: LayoutDashboard },
        { label: "POS Billing", path: "/pos", icon: Calculator },
        { label: "Mark Attendance", path: "/sales-executive/attendance", icon: CalendarCheck },
        { label: "Invoice History", path: "/invoices", icon: FileSpreadsheet },
        { label: "Track Collections", path: "/sales-executive/collections", icon: HandCoins },
        { label: "Apply Leave", path: "/sales-executive/leaves", icon: CalendarRange },
        { label: "Salary & Incentives", path: "/sales-executive/incentives-payroll", icon: Landmark },
        { label: "Support Ticket", path: "/support", icon: HelpCircle },
      ];
    }

    if (userRole === "OWNER") {
      return [
        { label: "Dashboard", path: "/owner/dashboard", icon: LayoutDashboard },
        { label: "POS Billing", path: "/pos", icon: Calculator },
        { label: "Invoice History", path: "/invoices", icon: FileSpreadsheet },
        { label: "Inventory", path: "/inventory", icon: Package },
        { label: "Categories", path: "/owner/categories", icon: Compass },
        { label: "Customers", path: "/customers", icon: Users },
        { label: "Support Desk", path: "/support", icon: HelpCircle },
      ];
    }

    // Default Fallback
    return [
      { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
      { label: "POS Billing", path: "/pos", icon: Calculator },
      { label: "Invoice History", path: "/invoices", icon: FileSpreadsheet },
      { label: "Customers", path: "/customers", icon: Users },
      { label: "Support", path: "/support", icon: HelpCircle },
    ];
  };

  const menuItems = getMenuItems();
  const userInitials = user?.first_name?.charAt(0) || user?.name?.charAt(0) || "U";
  const userDisplayName = user?.first_name || user?.name || "Staff Member";
  const salesmanId = user?.salesman_id || "";

  const handleNavigate = (path) => {
    onClose();
    if (path === "/pos" && window.location.pathname === "/pos") {
      // Already on POS billing page
      return;
    }
    navigate(path);
  };

  const handleLogoutClick = () => {
    onClose();
    logout();
    navigate("/login");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop Blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm cursor-pointer"
          />

          {/* Sliding Drawer Container */}
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed left-0 top-0 bottom-0 w-80 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 shadow-2xl z-[101] flex flex-col"
          >
            {/* Drawer Header */}
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-950/20">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-green-500 to-emerald-400 flex items-center justify-center shadow-md shadow-green-500/10">
                  <Calculator className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-black text-slate-800 dark:text-slate-100 leading-none">gio billing</h2>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mt-1 inline-block">POS Module</span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl transition-all active:scale-95"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Profile Context */}
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center gap-4 bg-slate-50/20 dark:bg-slate-950/10">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-lg font-black shadow-lg shadow-green-500/15">
                {userInitials}
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-black text-slate-800 dark:text-slate-100 truncate leading-tight">
                  {userDisplayName}
                </h4>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mt-0.5">
                  {salesmanId ? `Sales Executive (${salesmanId})` : userRole?.replace("_", " ") || "Staff"}
                </p>
              </div>
            </div>

            {/* Navigation Options */}
            <div className="flex-1 overflow-y-auto py-4 px-4 space-y-1.5 custom-scrollbar">
              <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block px-3 mb-3">Navigation Menu</span>
              {menuItems.map((item, idx) => {
                const Icon = item.icon;
                const isPos = item.path === "/pos" && window.location.pathname === "/pos";
                
                return (
                  <button
                    key={idx}
                    onClick={() => handleNavigate(item.path)}
                    className={`w-full flex items-center justify-between p-3.5 rounded-2xl transition-all group active:scale-[0.98] ${
                      isPos 
                        ? "bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20 shadow-xs" 
                        : "hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                    }`}
                  >
                    <div className="flex items-center gap-3.5">
                      <div className={`p-2 rounded-xl transition-all ${
                        isPos
                          ? "bg-green-500 text-white shadow-md shadow-green-500/20"
                          : "bg-slate-50 dark:bg-slate-800 group-hover:bg-white dark:group-hover:bg-slate-700 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200"
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-bold tracking-wide">{item.label}</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 transition-all opacity-0 group-hover:opacity-100 ${
                      isPos ? "text-green-500 translate-x-0.5 opacity-100" : "text-slate-400 group-hover:translate-x-0.5"
                    }`} />
                  </button>
                );
              })}
            </div>

            {/* Drawer Footer */}
            <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/20">
              <button
                onClick={handleLogoutClick}
                className="w-full flex items-center justify-center gap-2.5 p-3.5 bg-red-50 dark:bg-red-950/20 hover:bg-red-500 hover:text-white dark:hover:bg-red-600 dark:hover:text-white text-red-500 rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-[0.98]"
              >
                <LogOut className="w-4 h-4" />
                Sign Out Session
              </button>
              <div className="text-center mt-3">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                  Gio Billing &copy; {new Date().getFullYear()}
                </span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
